import { useState, useEffect, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  getPublishingDashboard,
  getPublishingDetail,
  approveSubmission,
  rejectSubmission,
  publishSubmission,
} from "../../api/clientPublishingApi";
import { formatDateTime } from "../../components/midpoint/admin/users/adminUserFormatters";
import "../../styles/clientPublishing.css";

const STATUS_BADGE = {
  PENDING_REVIEW: "pending",
  PUBLISHED: "published",
  APPROVED: "approved",
  REJECTED: "rejected",
  DRAFT: "draft",
};

function Badge({ status, label }) {
  return (
    <span className={`cpub-badge cpub-badge--${STATUS_BADGE[status] || "draft"}`}>
      {label || status}
    </span>
  );
}

function Toast({ message, type, onDismiss }) {
  if (!message) return null;
  return (
    <div className={`cpub-toast cpub-toast--${type}`}>
      <span>{message}</span>
      <button className="cpub-toast__dismiss" onClick={onDismiss} aria-label="Dismiss">
        &times;
      </button>
    </div>
  );
}

function SubmissionRow({ item, onClick }) {
  const isCourse = item.contentType === "course";
  const time = item.submittedAt || item.publishedAt || item.updatedAt;
  return (
    <div className="cpub-row" onClick={() => onClick(item)} role="button" tabIndex={0}>
      <div className={`cpub-row__icon${isCourse ? " cpub-row__icon--course" : ""}`}>
        {isCourse ? "⛳" : "🏘️"}
      </div>
      <div className="cpub-row__body">
        <div className="cpub-row__title">{item.title || "Untitled"}</div>
        <div className="cpub-row__meta">
          <span>{isCourse ? "Course" : "Community"}</span>
          <span className="cpub-row__meta-sep">&middot;</span>
          <span>v{item.versionNumber}</span>
          {item.submittedByEmail && (
            <>
              <span className="cpub-row__meta-sep">&middot;</span>
              <span>{item.submittedByEmail}</span>
            </>
          )}
          {time && (
            <>
              <span className="cpub-row__meta-sep">&middot;</span>
              <span>{formatDateTime(time)}</span>
            </>
          )}
        </div>
      </div>
      <Badge status={item.publishStatus} label={item.publishStatusLabel} />
    </div>
  );
}

function SnapshotPreview({ snapshot, contentType }) {
  if (!snapshot) return null;
  const fields =
    contentType === "course"
      ? [
          ["Course Name", snapshot.courseName],
          ["Slug", snapshot.slug],
          ["Description", snapshot.shortDescription],
          ["Display Status", snapshot.displayStatus],
          ["City", snapshot.city],
          ["Region", snapshot.stateRegion || snapshot.region],
        ]
      : [
          ["Community Name", snapshot.communityName],
          ["Slug", snapshot.slug],
          ["Description", snapshot.shortDescription],
          ["Display Status", snapshot.displayStatus],
          ["City", snapshot.city],
          ["Region", snapshot.stateRegion || snapshot.region],
        ];

  return (
    <div className="cpub-detail__snapshot">
      <div className="cpub-detail__snapshot-title">Content Snapshot</div>
      <dl>
        {fields.map(
          ([label, val]) =>
            val && (
              <div key={label} style={{ display: "contents" }}>
                <dt>{label}</dt>
                <dd>{val}</dd>
              </div>
            )
        )}
      </dl>
    </div>
  );
}

function ReviewDetail({ versionId, clientViewId, onBack, onActionComplete }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comments, setComments] = useState("");
  const [acting, setActing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    getPublishingDetail(clientViewId, versionId)
      .then((data) => setDetail(data))
      .catch((e) => setError(e.message || "Failed to load detail."))
      .finally(() => setLoading(false));
  }, [clientViewId, versionId]);

  const act = async (action, body = {}) => {
    setActing(true);
    setFeedback(null);
    try {
      if (action === "approve") {
        await approveSubmission(clientViewId, versionId, { reviewComments: comments || undefined });
        setFeedback({ type: "success", message: "Submission approved." });
      } else if (action === "reject") {
        if (!comments.trim()) {
          setFeedback({ type: "error", message: "Review comments are required when rejecting." });
          setActing(false);
          return;
        }
        await rejectSubmission(clientViewId, versionId, { reviewComments: comments });
        setFeedback({ type: "success", message: "Submission rejected." });
      } else if (action === "publish") {
        await publishSubmission(clientViewId, versionId);
        setFeedback({ type: "success", message: "Content published successfully." });
      }
      setTimeout(() => onActionComplete(), 800);
    } catch (e) {
      setFeedback({ type: "error", message: e.message || "Action failed." });
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <div className="cpub-detail">
        <div className="cpub-detail__body">
          <div className="cpub-skel">
            <div className="cpub-skel__bar cpub-skel__bar--tall cpub-skel__bar--wide" />
            <div className="cpub-skel__bar cpub-skel__bar--med" />
            <div className="cpub-skel__bar cpub-skel__bar--short" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpub-detail">
        <div className="cpub-detail__body">
          <Toast message={error} type="error" onDismiss={() => setError("")} />
          <button className="cpub-btn cpub-btn--ghost" onClick={onBack}>Back</button>
        </div>
      </div>
    );
  }

  const v = detail?.version;
  const snap = detail?.snapshot;
  const isPending = v?.publishStatus === "PENDING_REVIEW";
  const isApproved = v?.publishStatus === "APPROVED";

  return (
    <>
      <button className="cpub-back" onClick={onBack}>&larr; Back to queue</button>

      {feedback && (
        <Toast message={feedback.message} type={feedback.type} onDismiss={() => setFeedback(null)} />
      )}

      <div className="cpub-detail">
        <div className="cpub-detail__header">
          <div className="cpub-detail__header-left">
            <h2 className="cpub-detail__title">{v?.title || "Untitled"}</h2>
            <div className="cpub-detail__meta">
              <span>{v?.contentType === "course" ? "Course" : "Community"}</span>
              <span>v{v?.versionNumber}</span>
              {v?.submittedByEmail && <span>by {v.submittedByEmail}</span>}
              {v?.submittedAt && <span>{formatDateTime(v.submittedAt)}</span>}
            </div>
          </div>
          <Badge status={v?.publishStatus} label={v?.publishStatusLabel} />
        </div>

        <div className="cpub-detail__body">
          <SnapshotPreview snapshot={snap} contentType={v?.contentType} />

          {v?.changeSummary && (
            <p style={{ fontSize: "13px", color: "var(--cp-muted)" }}>
              <strong>Change summary:</strong> {v.changeSummary}
            </p>
          )}

          {v?.reviewComments && (
            <p style={{ fontSize: "13px", color: "var(--cp-red)" }}>
              <strong>Previous review:</strong> {v.reviewComments}
            </p>
          )}

          {(isPending || isApproved) && (
            <>
              <textarea
                className="cpub-comments"
                placeholder={isPending ? "Review comments (required for rejection)..." : "Optional notes..."}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                disabled={acting}
              />
            </>
          )}
        </div>

        {(isPending || isApproved) && (
          <div className="cpub-detail__actions">
            {isPending && (
              <>
                <button
                  className="cpub-btn cpub-btn--success"
                  disabled={acting}
                  onClick={() => act("approve")}
                >
                  {acting ? "Processing..." : "Approve"}
                </button>
                <button
                  className="cpub-btn cpub-btn--danger"
                  disabled={acting}
                  onClick={() => act("reject")}
                >
                  Reject
                </button>
                <button
                  className="cpub-btn cpub-btn--primary"
                  disabled={acting}
                  onClick={() => act("publish")}
                >
                  Approve & Publish
                </button>
              </>
            )}
            {isApproved && !isPending && (
              <button
                className="cpub-btn cpub-btn--primary"
                disabled={acting}
                onClick={() => act("publish")}
              >
                {acting ? "Publishing..." : "Publish Now"}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
}

const ClientPublishingPage = () => {
  const { clientViewId } = useParams();
  const { permissions } = useOutletContext() || {};
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedVersionId, setSelectedVersionId] = useState(null);

  const loadDashboard = useCallback(() => {
    setLoading(true);
    setError("");
    getPublishingDashboard(clientViewId)
      .then((data) => setDashboard(data))
      .catch((e) => setError(e.message || "Failed to load publishing dashboard."))
      .finally(() => setLoading(false));
  }, [clientViewId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  if (selectedVersionId) {
    return (
      <div className="cpub">
        <ReviewDetail
          versionId={selectedVersionId}
          clientViewId={clientViewId}
          onBack={() => {
            setSelectedVersionId(null);
            loadDashboard();
          }}
          onActionComplete={() => {
            setSelectedVersionId(null);
            loadDashboard();
          }}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cpub">
        <div className="cpub-header">
          <div className="cpub-header__left">
            <h1 className="cpub-header__title">Publishing</h1>
            <p className="cpub-header__subtitle">Manage content review and publishing</p>
          </div>
        </div>
        <div className="cpub-stats">
          {[1, 2, 3, 4].map((i) => (
            <div className="cpub-stat" key={i}>
              <div className="cpub-skel__bar cpub-skel__bar--tall cpub-skel__bar--short" />
              <div className="cpub-skel__bar cpub-skel__bar--med" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpub">
        <div className="cpub-header">
          <div className="cpub-header__left">
            <h1 className="cpub-header__title">Publishing</h1>
          </div>
        </div>
        <Toast message={error} type="error" onDismiss={() => setError("")} />
      </div>
    );
  }

  const stats = dashboard?.stats || {};
  const pending = dashboard?.pending || [];
  const pendingTotal = dashboard?.pendingTotal || 0;
  const recent = dashboard?.recent || [];

  return (
    <div className="cpub">
      <div className="cpub-header">
        <div className="cpub-header__left">
          <h1 className="cpub-header__title">Publishing</h1>
          <p className="cpub-header__subtitle">Review submissions, approve content, and track publishing activity</p>
        </div>
      </div>

      <div className="cpub-stats">
        <div className="cpub-stat cpub-stat--pending">
          <div className="cpub-stat__value">{stats.PENDING_REVIEW || 0}</div>
          <div className="cpub-stat__label">Pending Review</div>
        </div>
        <div className="cpub-stat cpub-stat--draft">
          <div className="cpub-stat__value">{stats.DRAFT || 0}</div>
          <div className="cpub-stat__label">Drafts</div>
        </div>
        <div className="cpub-stat cpub-stat--published">
          <div className="cpub-stat__value">{stats.PUBLISHED || 0}</div>
          <div className="cpub-stat__label">Published</div>
        </div>
        <div className="cpub-stat cpub-stat--rejected">
          <div className="cpub-stat__value">{stats.REJECTED || 0}</div>
          <div className="cpub-stat__label">Rejected</div>
        </div>
      </div>

      <div className="cpub-grid">
        <div>
          <div className="cpub-section">
            <div className="cpub-section__header">
              <h2 className="cpub-section__title">Pending Review</h2>
              {pendingTotal > 0 && <span className="cpub-section__count">{pendingTotal}</span>}
            </div>
            <div className="cpub-section__body">
              {pending.length === 0 ? (
                <div className="cpub-section__empty">No submissions awaiting review.</div>
              ) : (
                pending.map((item) => (
                  <SubmissionRow
                    key={item.contentVersionId}
                    item={item}
                    onClick={(it) => setSelectedVersionId(it.contentVersionId)}
                  />
                ))
              )}
            </div>
          </div>
        </div>

        <div>
          <div className="cpub-section">
            <div className="cpub-section__header">
              <h2 className="cpub-section__title">Recent Activity</h2>
            </div>
            <div className="cpub-section__body">
              {recent.length === 0 ? (
                <div className="cpub-section__empty">No recent publishing activity.</div>
              ) : (
                recent.map((item) => (
                  <SubmissionRow
                    key={item.contentVersionId}
                    item={item}
                    onClick={(it) => setSelectedVersionId(it.contentVersionId)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientPublishingPage;
